
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      title: "From Furnished Rentals to Investment Guidance:",
      subtitle: "Your Partner in Wealth-Building",
      description: "We specialize in short- and mid-term furnished rentals, property management, and investment guidance—helping guests, owners, and investors create lasting value.",
      image: "https://public.readdy.ai/ai/img_res/7da5adaa52e56281c298702dc069c5d7.jpg",
      badge: "4.9 Stars from 200+ Guests Review"
    },
    {
      title: "Stress-free Hosting",
      subtitle: "Professional Solutions",
      description: "Expert property management and consulting services that maximize your investment returns while providing exceptional guest experiences.",
      image: "https://readdy.ai/api/search-image?query=Attractive%20young%20couple%20between%2035-40%20years%20old%2C%20white%20male%20and%20Asian%20female%2C%20both%20wearing%20casual%20comfortable%20clothing%20like%20polo%20shirts%20and%20khakis%2C%20standing%20in%20front%20of%20beautiful%20suburban%20house%20with%20white%20picket%20fence%2C%20warm%20genuine%20welcoming%20smiles%2C%20holding%20house%20keys%2C%20relaxed%20casual%20attire%2C%20cozy%20residential%20neighborhood%20setting%2C%20bright%20sunny%20day%20with%20natural%20lighting%2C%20approachable%20friendly%20homeowners%20aged%2035-40%2C%20comfortable%20suburban%20home%20atmosphere%2C%20inviting%20welcoming%20gesture%2C%20youthful%20professional%20appearance&width=1920&height=1080&seq=hero-young-couple-35-40-suburban&orientation=landscape",
      badge: "98% Client Satisfaction"
    },
    {
      title: "Investment Guidance & Growth:",
      subtitle: "Build or Expand Your Portfolio",
      description: "Smart financing solutions and expert connections to help investors build and scale their real estate portfolios with confidence.",
      image: "https://readdy.ai/api/search-image?query=Bright%20modern%20financial%20planning%20office%20with%20investment%20charts%2C%20growth%20graphs%2C%20professional%20consultation%2C%20contemporary%20workspace%20with%20abundant%20natural%20lighting%2C%20success%20metrics%20on%20screens%2C%20clean%20business%20environment%2C%20well-lit%20bright%20office%2C%20sunny%20atmosphere&width=1920&height=1080&seq=hero-slide-bright-3&orientation=landscape",
      badge: "$50M+ in Funding Secured"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section 
      id="home"
      className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.3)), url('${slides[currentSlide].image}')`
      }}
    >
      {/* Background elements with darker overlay for better text contrast */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-black/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-black/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-black/20"></div>
      </div>

      {/* Carousel Navigation Arrows */}
      <button 
        onClick={prevSlide}
        className="absolute left-8 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-slate-900 hover:bg-white transition-all duration-300 cursor-pointer shadow-lg"
      >
        <i className="ri-arrow-left-line text-xl"></i>
      </button>
      
      <button 
        onClick={nextSlide}
        className="absolute right-8 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-slate-900 hover:bg-white transition-all duration-300 cursor-pointer shadow-lg"
      >
        <i className="ri-arrow-right-line text-xl"></i>
      </button>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
        <div className="text-center text-white">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full text-sm font-medium mb-8 border border-white/40 shadow-lg text-slate-800">
            <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></div>
            {slides[currentSlide].badge}
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight text-white drop-shadow-lg">
            {slides[currentSlide].title}
            <span className="block text-white/95">{slides[currentSlide].subtitle}</span>
          </h1>
          
          <p className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed text-white/90 drop-shadow-md">
            {slides[currentSlide].description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button size="lg" className="bg-slate-800 hover:bg-slate-700 text-white shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer">
              <i className="ri-search-line mr-2"></i>
              Explore Rentals
            </Button>
            <Button size="lg" className="bg-slate-800 hover:bg-slate-700 text-white shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer">
              <i className="ri-user-star-line mr-2"></i>
              Get Expert Advice
            </Button>
            <Button 
              size="lg" 
              className="bg-slate-800 hover:bg-slate-700 text-white shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              onClick={() => window.REACT_APP_NAVIGATE('/dscr')}
            >
              <i className="ri-funds-line mr-2"></i>
              Get Funded
            </Button>
            <Button size="lg" className="bg-slate-800 hover:bg-slate-700 text-white shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer">
              <i className="ri-shopping-bag-3-line mr-2"></i>
              Shop Host Toolkit
            </Button>
          </div>

        </div>
      </div>

      {/* Carousel Indicators */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
              index === currentSlide 
                ? 'bg-white w-8' 
                : 'bg-white/60 hover:bg-white/80'
            }`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
        </div>
      </div>
    </section>
  );
}
